import { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { AlertCircle, ChevronDown, Copy, Loader2, MessageCircle } from 'lucide-react';
import {
  buildCourierWhatsAppLink,
  buildCourierPortalWhatsAppLink,
  isCourierAssignableOrder,
  isManualApprovalPending,
  MAX_COURIER_BATCH_ORDERS,
  useTranslation,
} from '@dukanti/shared';
import type { CourierAssignmentResult, StoreOrderRow, TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAssignOrdersToCourier } from '@/hooks/use-assign-orders-to-courier';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useCouriers } from '@/hooks/use-couriers';
import { useGetOrCreateCourierPortalLink } from '@/hooks/use-get-or-create-courier-portal-link';

export interface CourierAssignmentDialogProps {
  readonly open: boolean;
  readonly storeId: string;
  readonly storeName: string;
  readonly orders: readonly StoreOrderRow[];
  readonly onOpenChange: (open: boolean) => void;
  readonly onAssigned?: (result: CourierAssignmentResult) => void;
}

export function CourierAssignmentDialog({
  open,
  storeId,
  storeName,
  orders,
  onOpenChange,
  onAssigned,
}: CourierAssignmentDialogProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const couriersQuery = useCouriers(storeId, open);
  const assignMutation = useAssignOrdersToCourier(storeId);
  const portalLinkMutation = useGetOrCreateCourierPortalLink(storeId);
  const { copy, isCopying } = useCopyToClipboard();
  const [courierId, setCourierId] = useState('');
  const [assignedCourierId, setAssignedCourierId] = useState<string | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [result, setResult] = useState<CourierAssignmentResult | null>(null);

  const activeCouriers = useMemo(
    () => (couriersQuery.data ?? []).filter((courier) => courier.active),
    [couriersQuery.data],
  );
  const approvedActiveCouriers = useMemo(
    () => activeCouriers.filter((courier) => courier.verification_status === 'approved'),
    [activeCouriers],
  );
  const pendingActiveCouriers = activeCouriers.length - approvedActiveCouriers.length;
  const orderIds = useMemo(() => orders.map((order) => order.id), [orders]);
  const orderIdsKey = orderIds.join('|');
  const existingAssignments = useMemo(
    () =>
      orders
        .map((order) => order.courier_assignment ?? null)
        .filter((assignment): assignment is NonNullable<StoreOrderRow['courier_assignment']> =>
          Boolean(assignment),
        ),
    [orders],
  );
  const existingCourierIds = useMemo(
    () => Array.from(new Set(existingAssignments.map((assignment) => assignment.courier_id))),
    [existingAssignments],
  );
  const currentSharedAssignment =
    existingCourierIds.length === 1
      ? (existingAssignments.find(
          (assignment) => assignment.courier_id === existingCourierIds[0],
        ) ?? null)
      : null;
  const allSelectedOrdersHaveAssignments =
    orders.length > 0 && existingAssignments.length === orders.length;
  const shouldShowCurrentAssignmentNotice =
    allSelectedOrdersHaveAssignments && currentSharedAssignment !== null;
  const shouldShowMixedAssignmentNotice =
    existingAssignments.length > 0 && !shouldShowCurrentAssignmentNotice;
  const hasOrdered = orders.some((order) => order.status === 'ordered');
  const hasManualApprovalPendingOrder = orders.some((order) => isManualApprovalPending(order));
  const hasPickupOrder = orders.some((order) => order.delivery_method === 'pickup');
  const hasInvalidOrder = orders.some((order) => !isCourierAssignableOrder(order));
  const tooManyOrders = orderIds.length > MAX_COURIER_BATCH_ORDERS;
  const assignedCourier =
    approvedActiveCouriers.find((courier) => courier.id === assignedCourierId) ?? null;
  const isSubmitting = assignMutation.isPending || portalLinkMutation.isPending;
  const assignmentSucceeded = result?.success === true;
  const selectedCurrentCourier =
    currentSharedAssignment !== null && courierId === currentSharedAssignment.courier_id;
  const canShowCurrentCourierOption =
    currentSharedAssignment !== null &&
    !approvedActiveCouriers.some((courier) => courier.id === currentSharedAssignment.courier_id);
  const whatsappT = t as unknown as TFunction;
  const shareUrl = portalUrl ?? result?.manifest_url ?? null;
  const whatsappUrl =
    shareUrl && assignedCourier
      ? portalUrl
        ? buildCourierPortalWhatsAppLink({
            courierPhoneNumber: assignedCourier.phone_number,
            courierName: assignedCourier.name,
            storeName,
            portalUrl,
            locale: lang,
            t: whatsappT,
          })
        : buildCourierWhatsAppLink({
            courierPhoneNumber: assignedCourier.phone_number,
            courierName: assignedCourier.name,
            storeName,
            manifestUrl: shareUrl,
            locale: lang,
            t: whatsappT,
          })
      : null;

  function resetAndClose(): void {
    setCourierId('');
    setAssignedCourierId(null);
    setPortalUrl(null);
    setResult(null);
    onOpenChange(false);
  }

  useEffect(() => {
    if (!open) return;
    setCourierId(currentSharedAssignment?.courier_id ?? '');
    setAssignedCourierId(null);
    setPortalUrl(null);
    setResult(null);
  }, [currentSharedAssignment?.courier_id, open, orderIdsKey]);

  function submit(): void {
    if (
      assignmentSucceeded ||
      isSubmitting ||
      !courierId ||
      orders.length === 0 ||
      hasInvalidOrder ||
      tooManyOrders
    )
      return;

    const nextCourierId = courierId;
    setResult(null);
    setAssignedCourierId(null);
    setPortalUrl(null);
    assignMutation.mutate(
      { courierId: nextCourierId, orderIds },
      {
        onSuccess: (nextResult) => {
          setResult(nextResult);
          if (!nextResult.success) return;
          onAssigned?.(nextResult);
          setAssignedCourierId(nextCourierId);
          portalLinkMutation.mutate(nextCourierId, {
            onSuccess: (portalResult) => {
              if (portalResult.success && portalResult.portal_url) {
                setPortalUrl(portalResult.portal_url);
              }
            },
          });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : resetAndClose())}>
      <DialogContent dir={dir} data-testid="courier-assignment-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.orders.courierAssignment.assign' as TranslationKey)}</DialogTitle>
          <DialogDescription>
            {t('admin.orders.courierAssignment.selectedCount' as TranslationKey, {
              count: orderIds.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {hasOrdered ? (
            <div className="border-honey/40 bg-honey/10 text-foreground rounded-lg border p-3 text-start text-sm">
              {t('admin.orders.courierAssignment.noAutoShipNotice' as TranslationKey)}
            </div>
          ) : null}

          {shouldShowCurrentAssignmentNotice ? (
            <div className="border-border bg-admin-surface-muted text-foreground rounded-lg border p-3 text-start text-sm">
              {t('admin.orders.courierAssignment.currentlyAssigned' as TranslationKey, {
                name: currentSharedAssignment.courier_name,
              })}
            </div>
          ) : null}

          {shouldShowMixedAssignmentNotice ? (
            <div className="border-honey/40 bg-honey/10 text-foreground rounded-lg border p-3 text-start text-sm">
              {t('admin.orders.courierAssignment.mixedExistingAssignments' as TranslationKey)}
            </div>
          ) : null}

          {hasInvalidOrder || tooManyOrders ? (
            <div
              role="alert"
              className="border-destructive/25 bg-destructive/10 text-destructive flex items-start gap-2 rounded-lg border p-3 text-start text-sm"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                {t(
                  (hasPickupOrder
                    ? 'admin.orders.fulfillment.deliveryOnly'
                    : tooManyOrders
                      ? 'admin.orders.courierAssignment.errors.tooManyOrders'
                      : hasManualApprovalPendingOrder
                        ? 'admin.orders.courierAssignment.errors.manualApprovalRequired'
                        : 'admin.orders.courierAssignment.errors.invalidStatus') as TranslationKey,
                )}
              </span>
            </div>
          ) : null}

          <label
            className="block text-start text-sm font-medium"
            htmlFor="courier-assignment-select"
          >
            {t('admin.nav.couriers' as TranslationKey)}
          </label>
          <div className="relative">
            <select
              id="courier-assignment-select"
              value={courierId}
              onChange={(event) => setCourierId(event.target.value)}
              className="border-input bg-input-background text-foreground focus-visible:border-ring focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-lg border bg-none px-3 py-2 pe-10 text-start text-sm outline-none [-moz-appearance:none] [-webkit-appearance:none] focus-visible:ring"
              disabled={isSubmitting || assignmentSucceeded || approvedActiveCouriers.length === 0}
              data-testid="courier-assignment-select"
            >
              <option value="">
                {t('admin.orders.courierAssignment.chooseCourier' as TranslationKey)}
              </option>
              {canShowCurrentCourierOption ? (
                <option value={currentSharedAssignment.courier_id} disabled>
                  {currentSharedAssignment.courier_name}
                </option>
              ) : null}
              {approvedActiveCouriers.map((courier) => (
                <option key={courier.id} value={courier.id}>
                  {courier.name}
                </option>
              ))}
            </select>
            <ChevronDown
              className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
              aria-hidden
            />
          </div>

          {approvedActiveCouriers.length === 0 && !couriersQuery.isLoading ? (
            <p className="text-muted-foreground text-start text-sm">
              {t(
                (pendingActiveCouriers > 0
                  ? 'admin.orders.courierAssignment.noVerifiedCouriers'
                  : 'admin.orders.courierAssignment.noActiveCouriers') as TranslationKey,
              )}
            </p>
          ) : null}

          {result?.success && shareUrl ? (
            <div className="border-border bg-admin-surface-muted rounded-lg border p-3 text-start">
              <p className="text-foreground text-sm font-medium">
                {t('admin.orders.courierAssignment.linkGenerated' as TranslationKey)}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isCopying}
                  onClick={() => void copy(shareUrl)}
                >
                  <Copy className="size-4" aria-hidden />
                  <span>{t('admin.orders.courierAssignment.copyLink' as TranslationKey)}</span>
                </Button>
                {whatsappUrl ? (
                  <Button type="button" variant="outline" size="sm" asChild>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="size-4" aria-hidden />
                      <span>
                        {t('admin.orders.courierAssignment.resendWhatsApp' as TranslationKey)}
                      </span>
                    </a>
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={resetAndClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={
              isSubmitting ||
              assignmentSucceeded ||
              selectedCurrentCourier ||
              !courierId ||
              orderIds.length === 0 ||
              hasInvalidOrder ||
              tooManyOrders
            }
            onClick={submit}
            aria-busy={isSubmitting || undefined}
          >
            {isSubmitting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span>{t('admin.orders.courierAssignment.assign' as TranslationKey)}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
